import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Spacer,
  Text,
  useToast,
  useDisclosure,
  Input,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { Field, Form, Formik } from "formik";
import Select from "react-select";
import { useFaqsMaster } from "../../store/faq/reducer";
import {
  REQUEST_CREATE_MASTER_FAQS,
  REQUEST_MASTER_FAQS,
  REQUEST_UPDATE_MASTER_FAQS,
  SET_MESSAGE_MASTER_FAQS,
} from "../../store/faq/faqActionTypes";
import FaqAddCategoryModal from "../modals/FaqAddCategoryModal";

interface FormFieldProps {
  required?: boolean;
  name: string;
  fieldName: string;
  placeholder: string;
  type?: string;
  error?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  required,
  name,
  fieldName,
  placeholder,
  type = "text",
  error,
}) => (
  <FormControl isRequired={required} mb={4}>
    <FormLabel htmlFor={fieldName}>{name}</FormLabel>
    <Field name={fieldName}>
      {({ field }: any) => (
        <Input
          {...field}
          id={fieldName}
          placeholder={placeholder}
          type={type}
          border="1px solid #E0E0E0"
          _focus={{
            borderColor: "#E24441",
            boxShadow: "0 0 0 1px #E24441",
          }}
        />
      )}
    </Field>
    {error && (
      <Text color="red.500" fontSize="sm">
        {error}
      </Text>
    )}
  </FormControl>
);

interface Props {
  faqs?: any;
  isOpen: boolean;
  onClose: () => void;
  faqData?: any;
}

const addSchema = Yup.object().shape({
  question: Yup.string().required("Question is required"),
  answer: Yup.string().required("Answer is required"),
  status: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required("Status is required"),
});

const FaqsEditDrawer: React.FC<Props> = ({ faqData, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { busy, masterFaqs } = useFaqsMaster();
  const toast = useToast();
  console.log("mastersjfcnds", faqData);

  const statusOptions = [
    { label: "Publish", value: "publish" },
    { label: "Unpublish", value: "unpublish" },
  ];

  const initialValue = useMemo(() => {
    if (faqData) {
      const selectedStatus = statusOptions.find(
        (option) => option.value === faqData.status
      );

      return {
        question: faqData.question || "",
        answer: faqData.answer || "",
        status: selectedStatus || statusOptions[0],
      };
    }
    return {
      question: "",
      answer: "",
      status: statusOptions[0],
    };
  }, [faqData]);

  const onSubmit = (values: Record<string, any>) => {
    const payload: Record<string, any> = {
      question: values.question,
      answer: values.answer,
      status: values.status.value,
      sequence: faqData ? faqData.sequence : masterFaqs.length + 1,
    };

    if (faqData) {
      const data = { payload, _id: faqData._id };
      dispatch({
        type: REQUEST_UPDATE_MASTER_FAQS,
        data,
        onSuccess: () => {
          dispatch({ type: REQUEST_MASTER_FAQS });
          onClose();
        },
      });
      toast({
        title: "Success",
        description: "FAQS has been updated",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    } else {
      dispatch({
        type: REQUEST_CREATE_MASTER_FAQS,
        payload,
        onSuccess: () => {
          dispatch({ type: REQUEST_MASTER_FAQS });
          onClose();
        },
      });
      toast({
        title: "Success",
        description: "New FAQs has been created",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const prevFaqs = useMemo(() => {
    return masterFaqs;
  }, []);

  useEffect(() => {
    if (faqData) {
      const faqsFromStore = masterFaqs.find((r: any) => r._id === faqData._id);

      if (faqsFromStore !== faqData) {
        toast({
          title: "Success",
          description: "FAQS has been updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    } else {
      if (masterFaqs.length !== prevFaqs.length) {
        toast({
          title: "Success",
          description: "New FAQs has been created",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    }
  }, [masterFaqs, prevFaqs, faqData, onClose, toast]);

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      border: state.isFocused ? "1px solid #E24441" : "1px solid #E0E0E0",
      boxShadow: state.isFocused ? "0 0 0 1px #E24441" : "none",
      "&:hover": {
        borderColor: "#E24441",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };
  return (
    <Formik
      initialValues={initialValue}
      validationSchema={addSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched, submitForm }: any) => (
        <Form>
          <Drawer
            isOpen={isOpen}
            placement="right"
            size="lg"
            onClose={() => {
              onClose();
              dispatch({ type: SET_MESSAGE_MASTER_FAQS });
            }}
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerHeader
                backgroundColor="#E244411A"
                borderBottom="1px solid #E0E0E0"
              >
                <Flex>
                  {faqData ? "Edit Faq" : "Add Faq"}
                  <Spacer />
                  <img
                    src="/icons/close.svg"
                    alt=""
                    style={{ cursor: "pointer" }}
                    onClick={onClose}
                  />
                </Flex>
              </DrawerHeader>
              <DrawerBody>
                <FormField
                  required
                  name="Question"
                  fieldName="question"
                  placeholder="Enter question"
                  type="text"
                  error={touched.question && (errors.question as string)}
                />
                <FormField
                  required
                  name="Answer"
                  fieldName="answer"
                  placeholder="Enter answer"
                  type="text"
                  error={touched.answer && (errors.answer as string)}
                />
                <FormControl isRequired>
                  <HStack>
                    <FormLabel>Status:</FormLabel>
                  </HStack>
                  <Select
                    value={values.status}
                    name="status"
                    onChange={(event) => setFieldValue("status", event)}
                    options={statusOptions}
                    placeholder="Select status"
                    styles={customStyles}
                  />
                  {touched.status && errors.status && (
                    <Text color="red.500" fontSize="sm">
                      {errors.status as string}
                    </Text>
                  )}
                </FormControl>
              </DrawerBody>
              <DrawerFooter>
                <Button
                  variant="outline"
                  mr={3}
                  onClick={() => {
                    onClose();
                    dispatch({ type: SET_MESSAGE_MASTER_FAQS });
                  }}
                  disabled={busy}
                >
                  Close
                </Button>
                <Button
                  backgroundColor="#E24441"
                  color="white"
                  _hover={{
                    backgroundColor: "#C33A37",
                  }}
                  onClick={submitForm}
                  disabled={busy}
                >
                  {faqData ? "Update" : "Add Faq"}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </Form>
      )}
    </Formik>
  );
};

export default FaqsEditDrawer;
